// The platform's AI usage record (PLAT-07, D-068): the model, tokens and cost of every AI step a call went through,
// for the platform's own costing. Metering only: a call is its company, time and length, never its content.

export interface StepUsage {
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  /** Whether Gemini's raw counts were kept for this step (from 1 October 2026). */
  raw: boolean;
}

export interface CallUsage {
  /** The first characters of the recording's id: enough to find it, nothing about the call. */
  ref: string;
  org_name: string;
  processed_at: string;
  audio_seconds: number | null;
  billable_minutes: number | null;
  transcript_chars: number | null;
  transcription: StepUsage;
  analysis: StepUsage;
  cost_usd: number;
}

export interface ModelUsage {
  step: "transcription" | "analysis";
  model: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  /** Calls for which Gemini reported no output tokens. */
  without_output: number;
}

export interface OrgUsage {
  org_id: string;
  org_name: string;
  calls: number;
  audio_seconds: number;
  billable_minutes: number;
  transcript_chars: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface UsageTotals {
  calls: number;
  audio_seconds: number;
  billable_minutes: number;
  transcript_chars: number;
  transcription_input_tokens: number;
  transcription_output_tokens: number;
  transcription_cost_usd: number;
  transcriptions_without_output: number;
  /** The transcript length of those calls, for estimating the output Gemini did not report. */
  transcript_chars_without_output: number;
  analysis_input_tokens: number;
  analysis_output_tokens: number;
  analysis_cost_usd: number;
  cost_usd: number;
}

/** platform_ai_usage(): one period's AI use. */
export interface PlatformAiUsage {
  from: string;
  to: string;
  totals: UsageTotals;
  by_model: ModelUsage[];
  by_org: OrgUsage[];
  calls: CallUsage[];
}

/** What the transcription model charges per million output tokens, as the worker counts it (gemini/transcribe.ts). */
export const TRANSCRIPT_OUTPUT_USD_PER_MILLION = 12;

/**
 * Roughly how many characters one transcript token holds. Hinglish in Latin script holds about 4; Hindi in
 * Devanagari, most of what is transcribed, fewer. Only used where Gemini reported no output count.
 */
export const CHARS_PER_TOKEN = 2;

/** The transcript's output tokens and their cost, estimated from its length, for calls Gemini reported none for. */
export function estimatedTranscriptOutput(chars: number | null | undefined): { tokens: number; usd: number } {
  const tokens = Math.ceil(Math.max(0, chars ?? 0) / CHARS_PER_TOKEN);
  return { tokens, usd: (tokens * TRANSCRIPT_OUTPUT_USD_PER_MILLION) / 1_000_000 };
}

export interface CostSummary {
  /** What the record holds: Gemini's counts at its published prices. */
  recordedUsd: number;
  /** Recorded, plus an estimate for transcripts Gemini reported no output for. */
  estimatedUsd: number;
  /** How many calls' transcription output had to be estimated. */
  estimatedCalls: number;
  perCallUsd: number | null;
  perAudioMinuteUsd: number | null;
}

/** Totals for costing: what one call and one minute of call cost, recorded and with the estimate. */
export function costSummary(totals: UsageTotals): CostSummary {
  const estimatedUsd = totals.cost_usd + estimatedTranscriptOutput(totals.transcript_chars_without_output).usd;
  return {
    recordedUsd: totals.cost_usd,
    estimatedUsd,
    estimatedCalls: totals.transcriptions_without_output,
    perCallUsd: totals.calls > 0 ? estimatedUsd / totals.calls : null,
    perAudioMinuteUsd: totals.audio_seconds > 0 ? estimatedUsd / (totals.audio_seconds / 60) : null,
  };
}

/** The periods the console offers, in India time. */
export type UsagePeriod = "today" | "7d" | "month" | "last_month";

export function periodRange(period: UsagePeriod, now = new Date()): { from: string; to: string } {
  // Midnight in India (UTC+5:30, no daylight saving) of the day `now` falls on there.
  const ist = new Date(now.getTime() + 330 * 60_000);
  const day = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d) - 330 * 60_000);
  const y = ist.getUTCFullYear(), m = ist.getUTCMonth(), d = ist.getUTCDate();
  const end = new Date(now.getTime() + 60_000).toISOString();
  switch (period) {
    case "today":
      return { from: day(y, m, d).toISOString(), to: end };
    case "7d":
      return { from: day(y, m, d - 6).toISOString(), to: end };
    case "month":
      return { from: day(y, m, 1).toISOString(), to: end };
    case "last_month":
      return { from: day(y, m - 1, 1).toISOString(), to: day(y, m, 1).toISOString() };
  }
}

const csvCell = (v: string | number | null | undefined) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
};

/** The per-call record as a spreadsheet, one row per call, the same fields the console shows. */
export function usageCsv(calls: CallUsage[]): string {
  const header = [
    "ref",
    "company",
    "processed_at",
    "audio_seconds",
    "billable_minutes",
    "transcript_chars",
    "transcription_model",
    "transcription_input_tokens",
    "transcription_output_tokens",
    "transcription_cost_usd",
    "analysis_model",
    "analysis_input_tokens",
    "analysis_output_tokens",
    "analysis_cost_usd",
    "cost_usd",
    "estimated_transcript_output_tokens",
  ];
  const rows = calls.map((c) => [
    c.ref,
    c.org_name,
    c.processed_at,
    c.audio_seconds,
    c.billable_minutes,
    c.transcript_chars,
    c.transcription.model,
    c.transcription.input_tokens,
    c.transcription.output_tokens,
    c.transcription.cost_usd,
    c.analysis.model,
    c.analysis.input_tokens,
    c.analysis.output_tokens,
    c.analysis.cost_usd,
    c.cost_usd,
    c.transcription.model && !(c.transcription.output_tokens ?? 0)
      ? estimatedTranscriptOutput(c.transcript_chars).tokens
      : "",
  ]);
  return [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n") + "\n";
}
